"use strict";
// Sprint S6 — Experimental-bets completeness gate.
//
// The three "innovation bets" (Trust-Score SLAs, Federated PSI, browser-native
// ephemeral access) are deliberately shipped incomplete and must stay
// flag-gated OFF by default until their maturity score reaches the lift gate
// (completeness >= 6). This module is the single source of truth for which bets
// exist, what their current completeness score is, and how a tenant's
// `settings.features.bets.*` flags are resolved. Both the API Gateway (route
// middleware) and the dashboard (nav + route guards) import from here so the
// gate cannot drift between surfaces.
Object.defineProperty(exports, "__esModule", { value: true });
exports.ExperimentalBetDisabledError = exports.DEFAULT_BET_FLAGS = exports.EXPERIMENTAL_BET_KEYS = exports.EXPERIMENTAL_BETS = exports.LIFT_GATE_SCORE = void 0;
exports.resolveBetFlags = resolveBetFlags;
exports.isBetEnabled = isBetEnabled;
exports.assertBetEnabled = assertBetEnabled;
exports.isBetLiftEligible = isBetLiftEligible;
/**
 * Minimum maturity score a bet must reach before it may be un-gated.
 * Mirrors the sprint plan's "off by default until completeness >= 6" rule.
 */
exports.LIFT_GATE_SCORE = 6;
/**
 * The registry. Edit completeness scores here when re-scoring at quarter end.
 * Scores below LIFT_GATE_SCORE force the bet to remain flag-gated off.
 */
exports.EXPERIMENTAL_BETS = {
    trust_sla: {
        key: "trust_sla",
        label: "Trust-Score SLAs",
        // Lifted to the gate (>= LIFT_GATE_SCORE): Trust-Score SLA attainment
        // reporting is complete enough to ship. Tenants that opt in via
        // settings.features.bets.trust_sla now receive live data instead of a 404.
        completenessScore: 6.0,
    },
    federated_psi: {
        key: "federated_psi",
        label: "Federated PSI",
        completenessScore: 4.5,
    },
    browser_native: {
        key: "browser_native",
        label: "Browser-Native Ephemeral Access",
        completenessScore: 4.0,
    },
};
/** Ordered list of bet keys (useful for iteration / reporting). */
exports.EXPERIMENTAL_BET_KEYS = [
    "trust_sla",
    "federated_psi",
    "browser_native",
];
/** All bets OFF — the safe default for any tenant that hasn't opted in. */
exports.DEFAULT_BET_FLAGS = {
    trust_sla: false,
    federated_psi: false,
    browser_native: false,
};
function isRecord(v) {
    return typeof v === "object" && v !== null && !Array.isArray(v);
}
/**
 * Resolve per-bet enablement flags from tenant `settings`.
 *
 * Expected shape (booleans; defaults to OFF):
 *   settings.features.bets.trust_sla
 *   settings.features.bets.federated_psi
 *   settings.features.bets.browser_native
 *
 * Enforces the completeness gate: even if a tenant sets a flag to true, the bet
 * stays OFF while its registry completenessScore is below LIFT_GATE_SCORE. This
 * makes "off by default until completeness >= 6" an enforced invariant, not a
 * convention.
 */
function resolveBetFlags(settings) {
    const features = isRecord(settings.features) ? settings.features : {};
    const betsRaw = features.bets;
    const bets = isRecord(betsRaw) ? betsRaw : {};
    const read = (key) => {
        const tenantOptIn = bets[key] === true;
        if (!tenantOptIn)
            return false;
        // Tenant opted in — still enforce the completeness lift gate.
        return exports.EXPERIMENTAL_BETS[key].completenessScore >= exports.LIFT_GATE_SCORE;
    };
    return {
        trust_sla: read("trust_sla"),
        federated_psi: read("federated_psi"),
        browser_native: read("browser_native"),
    };
}
/** True when the bet is enabled for the resolved flags. */
function isBetEnabled(flags, key) {
    return flags[key] === true;
}
/** Error thrown by `assertBetEnabled` so callers can surface a stable reason. */
class ExperimentalBetDisabledError extends Error {
    key;
    constructor(key, message) {
        super(message ??
            `Experimental bet "${exports.EXPERIMENTAL_BETS[key].label}" is not enabled for this tenant.`);
        this.key = key;
        this.name = "ExperimentalBetDisabledError";
    }
}
exports.ExperimentalBetDisabledError = ExperimentalBetDisabledError;
/**
 * Throws `ExperimentalBetDisabledError` when the bet is not enabled. Used by the
 * API Gateway middleware so a disabled bet short-circuits to a 403/404 before
 * the route handler runs.
 */
function assertBetEnabled(flags, key) {
    if (!isBetEnabled(flags, key)) {
        throw new ExperimentalBetDisabledError(key);
    }
}
/**
 * True when a bet's completeness score has reached the lift gate. Used by
 * reporting / scoring tooling to flag bets that are eligible to be un-gated.
 */
function isBetLiftEligible(key) {
    return exports.EXPERIMENTAL_BETS[key].completenessScore >= exports.LIFT_GATE_SCORE;
}
//# sourceMappingURL=completeness-gate.js.map